import { useCallback, useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useDialogContext } from '../../../contexts/DialogContext';
import { window } from '../../../globals';
import { useDebouncedInputValue } from '../../../hooks';
import useGetUserMasquerade from '../../../hooks/api/masquerade/useGetUserMasquerade';
import { usePinnedOrganizationsData } from '../../../hooks/api/organization/usePinnedOrganizationsData';
import { useSearchOrganizations } from '../../../hooks/api/organization/useSearchOrganizations';
import { useChangeUserOrganization } from '../../../hooks/api/users/useChangeUserOrganization';
import { usePinnedOrganizations } from '../../../hooks/usePinnedOrganizations';
import { SearchInput } from '../../generic';
import Alert from '../../generic/Alert';
import InlineLoaderButton from '../../generic/Button/InlineLoaderButton';
import Card from '../../generic/Card/Card';
import type { OrganizationSearchResult } from '../../../hooks/api/organization/useSearchOrganizations';

import { OrganizationSearchList } from './OrganizationSearchList';

import styles from './ChangeOrgCard.module.less';

const ChangeOrgCard: VoidFunctionComponent = () => {
  const { value: searchQuery, onChange: onSearchChange, debouncedValue } = useDebouncedInputValue('');
  const { isPinned, togglePin, pinnedOrgIds } = usePinnedOrganizations();

  const { onConfirm } = useDialogContext();
  const { data: masqueradeData } = useGetUserMasquerade();

  // Query for searching organizations
  const {
    data: organizations = [],
    isLoading: isSearching,
    error: searchError,
  } = useSearchOrganizations(debouncedValue, true);

  // Mutation for changing user organization
  const { mutate: changeOrganization, isPending: isChanging } = useChangeUserOrganization(() => {
    window.location.reload();
  });

  // Fetch pinned organizations data
  const pinnedOrgIdsArray = useMemo(() => Array.from(pinnedOrgIds), [pinnedOrgIds]);
  const { organizations: pinnedOrganizations, isLoading: isPinnedLoading } =
    usePinnedOrganizationsData(pinnedOrgIdsArray);

  const performOrganizationChange = useCallback(async (orgId: number) => {
    const isMasquerading = !!masqueradeData?.userId;

    if (isMasquerading) {
      onConfirm({
        title: 'Change Organization',
        body: 'Changing organizations will remove your current masquerade session. Do you want to continue?',
        confirmText: 'Change Organization',
        onConfirm: () => changeOrganization({ userId: window.user?.id ?? 0, organizationId: orgId }),
      });
    } else {
      changeOrganization({ userId: window.user?.id ?? 0, organizationId: orgId });
    }
  }, [masqueradeData, onConfirm, changeOrganization]);

  const selectOrganization = useCallback(
    (org: OrganizationSearchResult) => {
      performOrganizationChange(org.id);
    },
    [performOrganizationChange],
  );

  const changeToFOSSA = useCallback(() => {
    performOrganizationChange(1);
  }, [performOrganizationChange]);

  return (
    <Card isFullWidth padding="medium">
      <Card.Header>Change Organization</Card.Header>
      <Card.Content>
        {searchError && (
          <Alert kind="error" header="Failed to search organizations">
            {searchError.message}
          </Alert>
        )}
        <div className={styles.searchInputContainer}>
          <SearchInput
            label="Search Organizations"
            placeholder="Search Organizations by Title or ID..."
            value={searchQuery}
            onChange={onSearchChange}
            isDisabled={isChanging}
          />
        </div>
        {debouncedValue && (
          <>
            <div className={styles.listHeader}>Search Results</div>
            <OrganizationSearchList
              organizations={organizations}
              isLoading={isSearching}
              onSelectOrganization={selectOrganization}
              isChanging={isChanging}
              isPinned={isPinned}
              onTogglePin={togglePin}
            />
            <br />
          </>
        )}
        {pinnedOrganizations.length > 0 && (
          <>
            <div className={styles.listHeader}>Pinned Organizations</div>
            <OrganizationSearchList
              organizations={pinnedOrganizations}
              isLoading={isPinnedLoading}
              onSelectOrganization={selectOrganization}
              isChanging={isChanging}
              isPinned={isPinned}
              onTogglePin={togglePin}
            />
            <br />
          </>
        )}
        {!debouncedValue && pinnedOrganizations.length === 0 && (
          <div className="text-muted">Search for organizations and pin them for quick access</div>
        )}
        <br />
        {!window.on_prem && (
          <InlineLoaderButton
            onClick={changeToFOSSA}
            type="secondary"
            isLoading={isChanging || isSearching}
            isDisabled={window.user?.organization?.id === 1}
          >
            Change to FOSSA
          </InlineLoaderButton>
        )}
      </Card.Content>
    </Card>
  );
};

export default ChangeOrgCard;
